import React, { useCallback, useMemo, useState } from "react";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { useLazyLoadQuery, graphql, useMutation } from "react-relay";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorScreenProps,
} from "@/src/TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { SearchInput } from "../Products/SearchInput";
import { FlashList } from "@shopify/flash-list";
import {
  AHIcon,
  AHSeparator,
  AHText,
  Box,
  EmployeeAvatar,
  ListItem,
  useLayoutContext,
  useTheme,
} from "arkh";
import { AccountCreateSelectEmployeeQuery } from "./__generated__/AccountCreateSelectEmployeeQuery.graphql";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import {
  createNativeStackNavigator,
  NativeStackNavigationProp,
} from "@react-navigation/native-stack";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";
import {
  MoreItem,
  useNavigationHeaderOptions,
} from "@/src/common/navigationHeaderOptions";
import { FormattedMessage, useIntl } from "react-intl";

import { SafeAreaProvider } from "react-native-safe-area-context";
import { ActivityIndicator, FlatList, Platform } from "react-native";
import { OrgButton } from "../OrgHome/OrgButton";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";

import { AccountCreateSelectEmployeeCreateEmployeeMutation } from "./__generated__/AccountCreateSelectEmployeeCreateEmployeeMutation.graphql";
import { KeyboardAwareFlashListScrollComponent } from "@/src/components/mobile/KeyboardAwareFlashListScrollComponent";
import { useCreateSharedDeviceAuthRequest } from "./useCreateSharedDeviceAuthRequest";

function ActionCreateGroupAccountCTA(props: {
  onPress: () => void;
  loading: boolean;
}) {
  const intl_tokens = useIntlTokens();
  return (
    <Box style={{ alignItems: "center" }} space="large">
      <AHIcon type="group-actor" size="medium" />
      <AHText spaceVertical="medium" textAlign="center">
        <FormattedMessage
          defaultMessage={`Don’t want to create a personal account?{br}Add a shared device instead.`}
          description="AccountCreateSelectEmployee - create group account"
          values={{
            br: <AHText>{"\n"}</AHText>,
          }}
        />
      </AHText>
      <OrgButton
        border="text-primary"
        tintColor="text-primary"
        backgroundColor="transparent"
        onPress={props.onPress}
        title={intl_tokens.AddSharedDevice}
        loading={props.loading}
      />
    </Box>
  );
}

type AccountCreateSelectEmployeeScreenProps = {
  navigation: CompositeNavigationProp<
    UserLoggedInNavigatorNavigationProp<"AccountCreateSelectEmployee">,
    NativeStackNavigationProp<ParamList, "AccountCreateSelectEmployeeScreen">
  >;
  route: RouteProp<ParamList, "AccountCreateSelectEmployeeScreen">;
};

function InnerAccountCreateSelectEmployee(
  props: {
    fetchKey: string;
  } & AccountCreateSelectEmployeeScreenProps
) {
  const [query, setQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const data = useLazyLoadQuery<AccountCreateSelectEmployeeQuery>(
    graphql`
      query AccountCreateSelectEmployeeQuery($storeId: ID) {
        store(id: $storeId) {
          hasEmployeeProvider
          employees(orderBy: NAME) {
            edges {
              node {
                id
                name
                ...Avatar_employee
                storeUser {
                  id
                  user {
                    email
                  }
                }
              }
            }
          }
        }
      }
    `,
    { storeId: props.route.params.storeId },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const listData = useMemo(() => {
    return (
      data.store.employees?.edges
        .filter((e) =>
          e.node.name?.toLocaleLowerCase().includes(query.toLocaleLowerCase())
        )
        .sort(
          (a, b) => (a.node.storeUser ? 1 : 0) - (b.node.storeUser ? 1 : 0)
        ) ?? []
    );
  }, [query, data.store.employees?.edges]);

  const renderItem = useCallback(
    ({ item }: { item: (typeof listData)[number] }) => {
      return (
        <Box style={{ opacity: item.node.storeUser ? 0.5 : 1 }}>
          <ListItem
            onPress={() => {
              props.navigation.replace("CreateAccount", {
                type: "INDIVIDUAL",
                employeeId: item.node.id,
                storeId: props.route.params.storeId,
              });
            }}
            disabled={!!item.node.storeUser}
            left={<EmployeeAvatar employee={item.node} size="large" />}
            right={!!item.node.storeUser ? null : <ListItem.NavArrow />}
            footer={
              item.node.storeUser?.user?.email ? (
                <AHText>{item.node.storeUser?.user?.email}</AHText>
              ) : null
            }
          >
            <ListItem.Title>{item.node.name}</ListItem.Title>
          </ListItem>
        </Box>
      );
    },
    [props.navigation, props.route.params.storeId]
  );

  const layout = useLayoutContext();

  const intl_tokens = useIntlTokens();

  const [createEmployee, createEmployeeLoading] =
    useMutation<AccountCreateSelectEmployeeCreateEmployeeMutation>(graphql`
      mutation AccountCreateSelectEmployeeCreateEmployeeMutation(
        $storeId: ID
        $name: String!
      ) {
        store(id: $storeId) {
          employeeCreate(input: { name: $name }) {
            employee {
              id
            }
          }
        }
      }
    `);

  const onPressCreateEmployee = useCallback(() => {
    createEmployee({
      variables: {
        storeId: props.route.params.storeId,
        name: query,
      },
      onCompleted: (data) => {
        if (!data.store.employeeCreate?.employee) {
          return;
        }
        props.navigation.replace("EmployeeEdit", {
          id: data.store.employeeCreate?.employee?.id,
          storeId: props.route.params.storeId,
          navigateDeprecatedToAccountCreateOnCreate: true,
        });
      },
    });
  }, [createEmployee, props.navigation, props.route.params.storeId, query]);

  const ListFooterComponent = useMemo(() => {
    return listData.length > 0 && !data.store.hasEmployeeProvider ? (
      <>
        {listData.length > 0 ? <AHSeparator /> : null}
        <ListItem
          onPress={() => {
            if (createEmployeeLoading) {
              return;
            }
            onPressCreateEmployee();
          }}
          right={<AHIcon type="add-circle" size="large" color="primary" />}
        >
          <ListItem.Title color="text-secondary">
            {intl_tokens.AddEmployee}
          </ListItem.Title>
        </ListItem>
      </>
    ) : null;
  }, [
    createEmployeeLoading,
    intl_tokens.AddEmployee,
    onPressCreateEmployee,
    listData.length,
    data.store.hasEmployeeProvider,
  ]);

  const theme = useTheme();
  const [createAuth, createAuthLoading] = useCreateSharedDeviceAuthRequest(
    props.route.params.storeId
  );

  const ListEmptyComponent = useMemo(() => {
    return (
      <>
        {!data.store.hasEmployeeProvider ? (
          <ListItem
            onPress={() => {
              onPressCreateEmployee();
            }}
            disabled={createEmployeeLoading}
            right={
              createEmployeeLoading ? (
                <ActivityIndicator size="small" color={theme.primary} />
              ) : (
                <AHIcon type="add-circle" size="large" color="primary" />
              )
            }
          >
            <ListItem.Title color="text-secondary">
              {intl_tokens.AddEmployee}
            </ListItem.Title>
          </ListItem>
        ) : null}
        {isSearching ? (
          <ActionCreateGroupAccountCTA
            loading={createAuthLoading}
            onPress={() => {
              createAuth().then((res) => {
                props.navigation.replace("UserAdminCreateGroupDevice", {
                  deviceCode: res.deviceCode,
                  expiresAt: res.expiresAt,
                });
              });
            }}
          />
        ) : null}
      </>
    );
  }, [
    data.store.hasEmployeeProvider,
    createEmployeeLoading,
    theme.primary,
    intl_tokens.AddEmployee,
    isSearching,
    createAuthLoading,
    onPressCreateEmployee,
    createAuth,
    props.navigation,
  ]);

  return (
    <>
      <Box color="surface-primary">
        <SearchInput
          query={query}
          isSearching={isSearching}
          hideSeparator
          showCancel
          onFocus={() => setIsSearching(true)}
          onBlur={() => {
            setQuery("");
            setIsSearching(false);
          }}
          onChange={(e) => setQuery(e.nativeEvent.text)}
        />
      </Box>
      <AHSeparator />
      {Platform.OS === "web" ? (
        <FlatList
          data={listData}
          renderItem={renderItem}
          ItemSeparatorComponent={AHSeparator}
          keyboardShouldPersistTaps="handled"
          keyExtractor={(item) => item.node.id}
          contentContainerStyle={{
            paddingBottom: layout.space.medium,
          }}
          ListFooterComponent={ListFooterComponent}
          ListEmptyComponent={ListEmptyComponent}
        />
      ) : (
        <FlashList
          data={listData}
          renderItem={renderItem}
          ItemSeparatorComponent={AHSeparator}
          keyboardShouldPersistTaps="handled"
          keyExtractor={(item) => item.node.id}
          contentContainerStyle={{
            paddingBottom: layout.space.medium,
          }}
          ListEmptyComponent={ListEmptyComponent}
          ListFooterComponent={ListFooterComponent}
          automaticallyAdjustsScrollIndicatorInsets
          renderScrollComponent={KeyboardAwareFlashListScrollComponent}
        />
      )}
    </>
  );
}

const AccountCreateSelectEmployeeScreen = (
  props: AccountCreateSelectEmployeeScreenProps
) => {
  return (
    <SafeAreaProvider>
      <LazyQueryNetworkBoundrary>
        {(fetchKey) => (
          <InnerAccountCreateSelectEmployee fetchKey={fetchKey} {...props} />
        )}
      </LazyQueryNetworkBoundrary>
    </SafeAreaProvider>
  );
};

type ParamList = {
  AccountCreateSelectEmployeeScreen: {
    storeId?: string;
  };
};

const Stack = createNativeStackNavigator<ParamList>();

export const AccountCreateSelectEmployee = withWebModal(
  (props: UserLoggedInNavigatorScreenProps<"AccountCreateSelectEmployee">) => {
    const getNavigationHeaderOptions = useNavigationHeaderOptions();
    const intl = useIntl();
    const intl_tokens = useIntlTokens();

    const more: MoreItem[] = [
      {
        label: (
          <FormattedMessage
            defaultMessage="Create Account Without Employee"
            description="AccountCreateSelectEmployee - without employee option"
          />
        ),
        onPress: () => {
          props.navigation.replace("CreateAccount", {
            type: "INDIVIDUAL",
            storeId: props.route.params.storeId,
          });
        },
        icon: "guest-actor",
        type: "navigation",
      },
      {
        label: intl_tokens.CreateGroupAccount,
        onPress: () => {
          props.navigation.replace("CreateAccount", {
            type: "GROUP",
            storeId: props.route.params.storeId,
          });
        },
        icon: "group-actor",
        type: "navigation",
      },
    ];

    return (
      <Stack.Navigator>
        <Stack.Screen
          name="AccountCreateSelectEmployeeScreen"
          component={AccountCreateSelectEmployeeScreen}
          initialParams={{
            storeId: props.route.params.storeId,
          }}
          options={getNavigationHeaderOptions({
            title: intl.formatMessage({
              defaultMessage: "Select Employee",
              description: "AccountCreateSelectEmployee - title",
            }),
            type: "CLOSE",
            onClose: () => props.navigation.goBack(),
            more,
          })}
        />
      </Stack.Navigator>
    );
  }
);
